<template>
    <CCard class="h-100 border-0 shadow-sm">
      <CCardHeader class="bg-transparent border-0 d-flex justify-content-between align-items-center p-4">
        <h5 class="mb-0">Bank Accounts</h5>
        <CDropdown 
          size="sm" 
          color="light"
          placement="bottom-end"
          class="dropdown-with-zindex"
        >
          <template #toggler-content>
            <CIcon name="cil-options" />
          </template>
          <CDropdownItem @click="$emit('add-account')">
            <CIcon name="cil-plus" class="mr-2" /> Add account
          </CDropdownItem>
          <CDropdownItem @click="$emit('refresh-accounts')">
            <CIcon name="cil-reload" class="mr-2" /> Refresh balances
          </CDropdownItem>
          <CDropdownItem @click="$emit('view-all-accounts')">
            <CIcon name="cil-list" class="mr-2" /> View all accounts
          </CDropdownItem>
        </CDropdown>
      </CCardHeader>
      
      <CCardBody class="pt-0 pb-4 px-4">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 mb-0 text-muted small">Loading accounts...</p>
        </div>
  
        <!-- Account List -->
        <div v-else-if="accounts && accounts.length > 0">
          <div 
            v-for="account in accounts" 
            :key="account.id" 
            class="account-item mb-4 hover-item"
            @click="$emit('account-clicked', account)"
          >
            <!-- Account Header -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center">
                <div class="account-logo mr-3">
                  <img 
                    v-if="account.bankLogo" 
                    :src="account.bankLogo" 
                    :alt="account.bank" 
                    class="bank-logo"
                    @error="handleImageError"
                  />
                  <div 
                    v-else 
                    class="c-avatar c-avatar-md bg-light d-flex align-items-center justify-content-center"
                  >
                    <CIcon name="cil-bank" class="text-muted" />
                  </div>
                </div>
                <div>
                  <div class="font-weight-bold account-name">{{ account.bank }}</div>
                  <small class="text-muted account-type">{{ account.accountType }}</small>
                </div>
              </div>
              
              <!-- Account Status -->
              <div class="text-right">
                <CBadge 
                  :color="getAccountStatusColor(account.status)" 
                  class="px-2 py-1 mb-1"
                >
                  {{ account.status }}
                </CBadge>
              </div>
            </div>
  
            <!-- Account Details -->
            <div class="account-details">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div class="font-weight-medium account-holder">{{ account.accountName }}</div>
                  <small class="text-muted">{{ account.formattedAccountNumber }}</small>
                </div>
                <div class="text-right">
                  <div class="font-weight-bold account-balance" :class="getBalanceTextColor(account.balance)">
                    {{ account.displayBalance }}
                  </div>
                  <small 
                    :class="account.change >= 0 ? 'text-success' : 'text-danger'"
                    class="balance-change"
                  >
                    {{ account.change >= 0 ? '+' : '' }}{{ account.change }}%
                  </small>
                </div>
              </div>
  
              <!-- Health Indicator -->
              <div class="account-health">
                <div class="d-flex justify-content-between small text-muted mb-1">
                  <span>Account Health: {{ account.healthLabel }}</span>
                  <span>{{ account.statementsCount || 0 }} statements</span>
                </div>
                <CProgress 
                  height="4px"
                  :value="account.healthPercentage" 
                  :color="account.healthColor" 
                  class="mb-1"
                />
              </div>
  
              <!-- Quick Actions -->
              <div class="account-actions mt-2">
                <CRow class="no-gutters">
                  <CCol>
                    <CButton 
                      color="light" 
                      size="sm" 
                      class="btn-block btn-action"
                      @click.stop="$emit('view-statements', account)"
                    >
                      <CIcon name="cil-file" class="action-icon" />
                      <span class="d-none d-md-inline">Statements</span>
                    </CButton>
                  </CCol>
                  <CCol>
                    <CButton 
                      color="light" 
                      size="sm" 
                      class="btn-block btn-action"
                      @click.stop="$emit('add-transaction', account)"
                    >
                      <CIcon name="cil-plus" class="action-icon" />
                      <span class="d-none d-md-inline">Transaction</span>
                    </CButton>
                  </CCol>
                </CRow>
              </div>
            </div>
  
            <!-- Last Updated -->
            <div class="d-flex justify-content-between align-items-center mt-2">
              <small class="text-muted">
                Last updated: {{ formatDate(account.lastUpdated) }}
              </small>
              <CIcon name="cil-chevron-right" class="text-muted" size="sm" />
            </div>
          </div>
  
          <!-- Summary Footer -->
          <div class="accounts-summary mt-3 pt-3 border-top">
            <CRow class="text-center">
              <CCol>
                <div class="summary-item">
                  <div class="font-weight-bold h6 mb-1">{{ formatCurrency(totalBalance) }}</div>
                  <small class="text-muted">Total Balance</small>
                </div>
              </CCol>
              <CCol>
                <div class="summary-item">
                  <div class="font-weight-bold h6 mb-1">{{ accounts.length }}</div>
                  <small class="text-muted">Active Accounts</small>
                </div>
              </CCol>
            </CRow>
          </div>
        </div>
  
        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="empty-state">
            <CIcon name="cil-bank" size="2xl" class="text-secondary mb-3" />
            <h6 class="mb-2">No Bank Accounts</h6>
            <p class="text-muted mb-3 small">
              Add your first bank account to start tracking your finances
            </p>
            <CButton 
              color="primary" 
              size="sm"
              @click="$emit('add-account')"
            >
              <CIcon name="cil-plus" /> Add Bank Account
            </CButton>
          </div>
        </div>
      </CCardBody>
  
      <!-- Widget Footer -->
      <CCardFooter class="bg-transparent border-0 pt-0 pb-3 px-4">
        <div class="d-flex justify-content-between align-items-center">
          <CButton 
            color="link" 
            size="sm" 
            class="p-0 text-primary"
            @click="$emit('view-all-accounts')"
          >
            View All Accounts
          </CButton>
          <CButton 
            color="light" 
            size="sm"
            @click="$emit('refresh-accounts')"
            :disabled="loading"
          >
            <CIcon name="cil-reload" :class="{ 'fa-spin': loading }" />
          </CButton>
        </div>
      </CCardFooter>
    </CCard>
  </template>
  
  <script>
  import { formatDateTime } from '@/utils/datetime';
  import { formatMyr } from '@/utils/money';
  
  export default {
    name: 'BankAccountsWidget',
    props: {
      accounts: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        default: false
      },
      currency: {
        type: String,
        default: 'MYR'
      }
    },
    computed: {
      totalBalance() {
        return this.accounts.reduce((sum, account) => {
          return sum + (account.balance || 0);
        }, 0);
      }
    },
    methods: {
      formatCurrency(amount) {
        return 'RM ' + formatMyr(amount);
      },
      formatDate(dateString) {
        if (!dateString) return 'Never';
        return formatDateTime(dateString);
      },
      getAccountStatusColor(status) {
        const statusColors = {
          'Active': 'success',
          'Inactive': 'secondary',
          'Closed': 'secondary',
          'Suspended': 'secondary'
        };
        return statusColors[status] || 'success';
      },
      getBalanceTextColor(balance) {
        if (!balance || balance < 0) return 'text-danger';
        if (balance < 1000) return 'text-warning';
        return 'text-success';
      },
      handleImageError(event) {
        // Hide broken image and show icon instead
        event.target.style.display = 'none';
        const parent = event.target.parentElement;
        parent.innerHTML = '<div class="c-avatar c-avatar-md bg-light d-flex align-items-center justify-content-center"><i class="cil-bank text-muted"></i></div>';
      }
    }
  };
  </script>
  
  <style scoped>
  /* Bank Accounts Widget Styling */
  .account-item {
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    background: #fff;
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .account-item:hover {
    border-color: #80bdff;
  }
  
  .account-item:last-child {
    margin-bottom: 0;
  }
  
  .account-logo {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .bank-logo {
    max-width: 36px;
    max-height: 36px;
    object-fit: contain;
    border-radius: 4px;
    padding: 2px;
  }
  
  .account-name {
    font-size: 0.9rem;
    color: #495057;
    margin-bottom: 1px;
  }
  
  .account-type {
    font-size: 0.75rem;
    text-transform: capitalize;
  }
  
  .account-holder {
    font-size: 0.85rem;
    color: #6c757d;
  }
  
  .account-balance {
    font-size: 1.1rem;
    line-height: 1.2;
  }
  
  .balance-change {
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .account-health {
    margin: 0.5rem 0;
  }
  
  .account-actions .btn-action {
    border: 1px solid #e9ecef;
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
    margin-right: 0.25rem;
    transition: all 0.15s ease;
  }
  
  .account-actions .btn-action:last-child {
    margin-right: 0;
  }
  
  .account-actions .btn-action:hover {
    border-color: #80bdff;
    background-color: #f8f9fa;
  }
  
  .action-icon {
    margin-right: 0.25rem;
  }
  
  .accounts-summary {
    background: #f8f9fa;
    border-radius: 0.375rem;
    padding: 0.75rem;
  }
  
  .summary-item {
    padding: 0.25rem;
  }
  
  .empty-state {
    padding: 1rem;
  }
  
  .hover-item {
    position: relative;
  }
  
  .hover-item::after {
    content: '';
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  .hover-item:hover::after {
    opacity: 1;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .account-item {
      padding: 0.75rem;
    }
    
    .account-logo {
      width: 40px;
      height: 40px;
    }
    
    .bank-logo {
      max-width: 30px;
      max-height: 30px;
    }
    
    .account-actions .btn-action {
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
    }
    
    .account-actions .btn-action span {
      display: none !important;
    }
  }
  
  /* Dropdown z-index fix */
  .dropdown-with-zindex {
    position: static;
  }
  
  .dropdown-with-zindex .dropdown-menu {
    z-index: 1050 !important;
    position: absolute;
    will-change: transform;
  }
  
  /* Progress bar styling */
  .progress {
    background-color: #e9ecef;
  }
  
  .progress-bar {
    transition: width 0.3s ease;
  }
  
  /* Badge improvements */
  .badge {
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.025em;
  }
  
  /* Animation for loading spinner */
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .fa-spin {
    animation: spin 1s linear infinite;
  }
  
  /* Button improvements */
  .btn-link {
    text-decoration: none;
    font-weight: 500;
  }
  
  .btn-link:hover {
    text-decoration: none;
  }
  
  /* Card footer improvements */
  .card-footer {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  /* Summary styling */
  .summary-item .h6 {
    color: #495057;
    margin-bottom: 0.25rem;
  }
  
  .summary-item small {
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }
  
  /* Empty state styling */
  .empty-state {
    max-width: 280px;
    margin: 0 auto;
  }
  
  .empty-state .btn {
    min-width: 140px;
  }
  
  /* Account details improvements */
  .account-details {
    background: rgba(0, 0, 0, 0.01);
    border-radius: 0.25rem;
    padding: 0.75rem;
    margin: 0.5rem 0;
  }
  
  /* Health indicator improvements */
  .account-health .progress {
    height: 4px;
    border-radius: 2px;
  }
  
  /* Last updated styling */
  .account-item > .d-flex:last-child {
    margin-top: 0.75rem;
    padding-top: 0.5rem;
    border-top: 1px solid #f1f3f4;
  }
  
  /* Icon improvements */
  .cil-chevron-right {
    transition: transform 0.2s ease;
  }
  
  .account-item:hover .cil-chevron-right {
    transform: translateX(2px);
  }
  </style>